import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { isImpersonating } from '@/auth';
import { useTheme } from '@/theme';
import { useTranslation } from '@/i18n';
import { useBreakpoint } from '@/hooks';
import { MySettingsModal } from './MySettingsModal';

interface UserMenuDropdownProps {
  userName: string;
  initials: string;
  profilePictureUrl?: string;
  avatarColor?: string;
  onLogout: () => void;
}

export const UserMenuDropdown = ({ userName, initials, profilePictureUrl, avatarColor, onLogout }: UserMenuDropdownProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showMySettings, setShowMySettings] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { theme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useBreakpoint();
  const isCompact = isMobile || isTablet;

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close on Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={t('User menu')}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? theme.spacing.xs : theme.spacing.sm,
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          padding: theme.spacing.xs,
          borderRadius: theme.borderRadius.md,
          flexShrink: 0,
        }}
      >
        <div
          style={{
            width: isMobile ? '28px' : '32px',
            height: isMobile ? '28px' : '32px',
            borderRadius: theme.borderRadius.full,
            backgroundColor: avatarColor || theme.colors.brandPrimary,
            color: 'white',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: isMobile ? theme.typography.fontSizeXs : theme.typography.fontSizeSm,
            fontWeight: theme.typography.fontWeightSemibold,
            flexShrink: 0,
            overflow: 'hidden',
          }}
        >
          {profilePictureUrl ? (
            <img
              src={profilePictureUrl}
              alt={userName}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            initials
          )}
        </div>
        {!isCompact && (
          <span
            style={{
              fontSize: theme.typography.fontSizeMd,
              fontWeight: theme.typography.fontWeightMedium,
              color: theme.colors.textPrimary,
              whiteSpace: 'nowrap',
            }}
          >
            {userName}
          </span>
        )}
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke={theme.colors.textSecondary}
          strokeWidth="2"
          style={{
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s',
          }}
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>

      {isOpen && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: theme.spacing.xs,
            backgroundColor: theme.colors.backgroundCard,
            borderRadius: theme.borderRadius.lg,
            boxShadow: '0 4px 24px rgba(0,0,0,0.12)',
            border: theme.borders.default,
            minWidth: '200px',
            zIndex: 1000,
            overflow: 'hidden',
          }}
        >
          {/* User info header */}
          <div
            style={{
              padding: `${theme.spacing.md} ${theme.spacing.lg}`,
              borderBottom: theme.borders.default,
            }}
          >
            <div
              style={{
                fontSize: theme.typography.fontSizeMd,
                fontWeight: theme.typography.fontWeightSemibold,
                color: theme.colors.textPrimary,
              }}
            >
              {userName}
            </div>
          </div>

          {/* Menu items */}
          <div style={{ padding: theme.spacing.xs }}>
            {/* My Account - hide during impersonation */}
            {!isImpersonating() && (
              <button
                role="menuitem"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/my-account');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing.sm,
                  width: '100%',
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  background: 'none',
                  border: 'none',
                  borderRadius: theme.borderRadius.md,
                  cursor: 'pointer',
                  color: theme.colors.textPrimary,
                  fontSize: theme.typography.fontSizeSm,
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.backgroundMuted;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                  <circle cx="12" cy="7" r="4" />
                </svg>
                {t('My Account')}
              </button>
            )}

            {/* Authority Delegation - hide during impersonation */}
            {!isImpersonating() && (
              <button
                role="menuitem"
                onClick={() => {
                  setIsOpen(false);
                  navigate('/authority-delegation');
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing.sm,
                  width: '100%',
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  background: 'none',
                  border: 'none',
                  borderRadius: theme.borderRadius.md,
                  cursor: 'pointer',
                  color: theme.colors.textPrimary,
                  fontSize: theme.typography.fontSizeSm,
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.backgroundMuted;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                  <circle cx="9" cy="7" r="4" />
                  <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                  <path d="M16 3.13a4 4 0 0 1 0 7.75" />
                </svg>
                {t('AuthorityDelegation:Title')}
              </button>
            )}

            {/* My Settings */}
            {!isImpersonating() && (
              <button
                role="menuitem"
                onClick={() => {
                  setIsOpen(false);
                  setShowMySettings(true);
                }}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: theme.spacing.sm,
                  width: '100%',
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  background: 'none',
                  border: 'none',
                  borderRadius: theme.borderRadius.md,
                  cursor: 'pointer',
                  color: theme.colors.textPrimary,
                  fontSize: theme.typography.fontSizeSm,
                  textAlign: 'left',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = theme.colors.backgroundMuted;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
                {t('MySettings')}
              </button>
            )}

            {/* Divider */}
            <div style={{ height: '1px', backgroundColor: theme.colors.border, margin: `${theme.spacing.xs} 0` }} />

            <button
              role="menuitem"
              onClick={() => {
                setIsOpen(false);
                onLogout();
              }}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: theme.spacing.sm,
                width: '100%',
                padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                background: 'none',
                border: 'none',
                borderRadius: theme.borderRadius.md,
                cursor: 'pointer',
                color: theme.colors.error,
                fontSize: theme.typography.fontSizeSm,
                textAlign: 'left',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = theme.colors.backgroundMuted;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'transparent';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line x1="21" y1="12" x2="9" y2="12" />
              </svg>
              {t('Logout')}
            </button>
          </div>
        </div>
      )}
      <MySettingsModal isOpen={showMySettings} onClose={() => setShowMySettings(false)} />
    </div>
  );
};
